import { makeStyles } from "@material-ui/core";

// Paleta / semântica (mantida): vermelho = atraso/risco, verde = no prazo,
// laranja = antecipado, cinza/slate = previsão. Visual modernizado (cards,
// slate header, zebra sutil, sombras).
const C = {
  bg: "#eef1f5",
  card: "#ffffff",
  border: "#e5e9f0",
  line: "#eef1f5",
  slate: "#1e293b",
  slate2: "#334155",
  text: "#1f2933",
  muted: "#6b7280",
  danger: "#fe0000",
  dangerSoft: "#fff5f5",
  green: "#12965a",
  greenSoft: "#4ade80",
  orange: "#FF6B00",
  orangeSoft: "#f59e0b",
  forecast: "#64748b",
};

// Altura da linha da transportadora expressa: três faixas empilhadas
// (line-height 1.55em sobre a fonte da tabela), mais os dois gaps de
// 2px e o padding vertical de 4px da linha.
//
// A tabela de valores e a de PREVISÃO são dois elementos lado a lado, então
// as duas precisam da MESMA altura nessa linha — senão as duas metades da
// mesma transportadora desalinham, e tudo abaixo dela junto.
// Fonte dos valores das DUAS tabelas — a principal e a de PREVISÃO ao lado.
// Elas formam uma linha só na tela, então um tamanho diferente em cada uma faz
// as alturas divergirem e a linha ir desalinhando de cima pra baixo.
const TABLE_FONT_SIZE = "0.83vw";

const EXPRESS_STACK_GAP = 10;
const EXPRESS_ROW_HEIGHT =
  `calc(3 * 1.55 * ${TABLE_FONT_SIZE} + ${2 * EXPRESS_STACK_GAP + 8}px)`;

// Cor de cada porte de pedido (campo order_size da transportadora).
// Usada na borda lateral e no texto da linha, e no resumo por porte.
const ORDER_SIZE_COLORS = {
  pequeno: "#FF6B00",
  medio: "#F71963",
  grande: "#12A150",
};

const useStyle = makeStyles((theme) => ({
  // Pulse original do card "PRÓXIMA COLETA": borda vermelha + pisca suave
  // com onda de sombra expandindo. 1.6s ease-in-out infinite.
  "@keyframes nextCollectionPulse": {
    "0%": { boxShadow: "0 0 0 0 rgba(254,0,0,.55)", background: "#fff" },
    "50%": { boxShadow: "0 0 0 14px rgba(254,0,0,0)", background: "#fff5f5" },
    "100%": { boxShadow: "0 0 0 0 rgba(254,0,0,0)", background: "#fff" },
  },

  // O pisca da faixa FALTAM 30M não está aqui: ele reusa o `pulse` do <Row>
  // (index.js), o mesmo da coleta que está a 1h de fechar. Repetir os
  // keyframes aqui daria dois piscas fora de sincronia na mesma tela.

  fullwidth: {
    // width: 100% respeita o espaço disponível (100vw estourava a viewport
    // porque inclui a scrollbar vertical, gerando scroll horizontal na tela).
    width: "100%",
    minHeight: "100vh",
    padding: 18,
    display: "flex",
    flexDirection: "column",
    gap: 16,
    boxSizing: "border-box",
    background: C.bg,
    fontFamily: "'Inter', 'Segoe UI', Roboto, Arial, sans-serif",
    overflowX: "hidden",

    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  // ── HEADER (KPIs em cards) ───────────────────────────────────────────────
  // Otimizado pra TV 42-49" em fullscreen — padding vertical compacto, mais
  // espaço vertical pra tabela crescer.
  boxHead: {
    display: "flex",
    alignItems: "stretch",
    gap: 10,

    "& h1": {
      fontSize: "1.5vw",
      fontWeight: 800,
      color: C.slate,
      letterSpacing: "-0.01em",
      alignSelf: "center",
      marginRight: 6,
    },

    "& .kpi": {
      background: C.card,
      border: `1px solid ${C.border}`,
      borderRadius: 14,
      boxShadow: "0 1px 3px rgba(16,24,40,.06)",
      padding: "6px 20px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "8vw",

      "& .label": {
        fontSize: "0.75vw",
        fontWeight: 700,
        color: C.muted,
        textTransform: "uppercase",
        letterSpacing: ".06em",
      },
      "& .value": {
        fontSize: "2vw",
        fontWeight: 800,
        color: C.text,
        lineHeight: 1.02,
      },
      "& .unit": {
        fontSize: "0.68vw",
        color: C.muted,
        textTransform: "uppercase",
        letterSpacing: ".05em",
      },

      "&.danger": {
        background: C.dangerSoft,
        borderColor: "#f7c4c4",
        "& .label": { color: C.danger },
        "& .value": { color: C.danger },
      },

      // Variante ALERTA (laranja) — clicável, abre modal
      "&.alert": {
        background: "#fffaf0",
        borderColor: "#fcd9a4",
        cursor: "pointer",
        transition: "transform .12s, box-shadow .12s",
        "& .label": { color: C.orange },
        "& .value": { color: C.orange },
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 6px 14px rgba(224,123,60,.18)",
        },
      },
    },

    // ── Grupo central: PRÓXIMA COLETA + DATA EMBARQUE lado a lado
    "& .centerGroup": {
      marginLeft: "auto",
      marginRight: "auto",
      display: "flex",
      alignItems: "stretch",
      gap: 12,
    },

    // ── PRÓXIMA COLETA — versão original piscando (borda vermelha + pulse)
    "& .nextCollection": {
      background: C.card,
      border: `2px solid ${C.danger}`,
      borderRadius: 14,
      padding: "6px 26px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "10vw",
      animation: "$nextCollectionPulse 1.6s ease-in-out infinite",

      "& .label": {
        fontSize: "0.8vw",
        fontWeight: 700,
        color: C.danger,
        textTransform: "uppercase",
        letterSpacing: ".06em",
      },
      "& .value": {
        fontSize: "2.4vw",
        fontWeight: 800,
        color: C.danger,
        lineHeight: 1,
        fontFamily: "'Space Mono', 'Roboto Mono', monospace",
        letterSpacing: ".02em",
      },
    },

    // ── DATA EMBARQUE — mesmos tamanhos/espaçamento do PRÓXIMA COLETA
    "& .dateEmbarque": {
      background: C.card,
      border: `1px solid ${C.border}`,
      borderRadius: 14,
      boxShadow: "0 1px 3px rgba(16,24,40,.06)",
      padding: "6px 26px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "10vw",

      "& .label": {
        fontSize: "0.8vw",
        fontWeight: 700,
        color: "#6b7280",
        textTransform: "uppercase",
        letterSpacing: ".06em",
      },
      "& .boxDateEmbarque": {
        display: "flex",
        alignItems: 'center',
        "& .date": {
          fontSize: "2.4vw",
          fontWeight: 800,
          color: "#6b7280",
          lineHeight: 1,
          fontFamily: "'Space Mono', 'Roboto Mono', monospace",
          letterSpacing: ".02em",
        },
        "& .time": {
          fontSize: "1.5vw",
          fontWeight: 700,
          color: "#6b7280",
          textAlign: "center",
          fontFamily: "'Space Mono', 'Roboto Mono', monospace",
          marginLeft: 15,
        },
      }
    },

    // ── Previsão ISOLADA: bloco separado visualmente com fundo slate,
    //    cores invertidas pra destacar da tabela principal. Clicável.
    "& .kpiForecast": {
      cursor: "pointer",
      transition: "transform .12s, box-shadow .12s",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: "0 8px 18px rgba(15,23,42,.28)",
      },
      background: C.slate,
      border: `1px solid ${C.slate2}`,
      borderRadius: 14,
      boxShadow: "0 4px 10px rgba(15,23,42,.18)",
      padding: "6px 18px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: 2,
      minWidth: "12vw",

      "& .fRow": {
        display: "flex",
        alignItems: "baseline",
        justifyContent: "flex-end",
        gap: 8,
        "& .label": {
          fontSize: "0.78vw",
          fontWeight: 700,
          color: "#cbd5e1",
          textTransform: "uppercase",
          letterSpacing: ".05em",
        },
        "& .value": {
          fontSize: "1.4vw",
          fontWeight: 800,
          color: "#fff",
        },
        "& .unit": {
          fontSize: "0.66vw",
          color: "#94a3b8",
          textTransform: "uppercase",
        },
        "&.sub": {
          borderTop: "1px dashed rgba(255,255,255,.15)",
          paddingTop: 4,
          marginTop: 2,
          "& .label": { fontSize: "0.7vw", color: "#94a3b8", fontWeight: 600 },
          "& .value": { fontSize: "1vw", color: "#cbd5e1" },
          "& .unit": { fontSize: "0.6vw" },
        },
      },
    },
  },

  // ── LAYOUT das 2 tabelas lado a lado (principal + previsão isolada) ──────
  // alignItems: flex-start impede as tabelas de esticarem verticalmente pra
  // preencher todo o espaço restante — cada uma cresce apenas até o conteúdo.
  // Sem maxHeight: se passar da viewport, a página inteira que rola.
  // ── FILTRO POR DATA ──────────────────────────────────────────────────────
  // Painel flutuante ancorado no canto inferior direito, logo acima do botão
  // que o abre. Fica fora do fluxo pra não empurrar o layout da TV.
  dateFilterPanel: {
    position: "fixed",
    right: 14,
    bottom: 60,
    zIndex: 900,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    maxWidth: "30vw",
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    boxShadow: "0 8px 28px rgba(16,24,40,.18)",
    padding: "12px 16px",

    "& .controls": {
      display: "flex",
      alignItems: "center",
      gap: 10,
    },

    "& .title": {
      fontSize: "0.75vw",
      fontWeight: 700,
      color: C.muted,
      textTransform: "uppercase",
      letterSpacing: ".06em",
    },

    "& .dateInput": {
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      padding: "6px 10px",
      fontSize: "0.85vw",
      fontWeight: 600,
      color: C.text,
      fontFamily: "inherit",
      background: "#fff",
      outline: "none",
      "&:focus": { borderColor: C.slate2 },
    },

    "& button": {
      border: "1px solid transparent",
      borderRadius: 10,
      padding: "7px 16px",
      fontSize: "0.78vw",
      fontWeight: 700,
      fontFamily: "inherit",
      cursor: "pointer",
      textTransform: "uppercase",
      letterSpacing: ".04em",
      transition: "filter .12s, background .12s",
      "&:disabled": { opacity: 0.45, cursor: "not-allowed" },
      "&:not(:disabled):hover": { filter: "brightness(1.06)" },
    },

    "& button.apply": {
      background: C.slate,
      color: "#fff",
    },

    "& button.clear": {
      background: "#fff",
      color: C.muted,
      borderColor: C.border,
      "&:not(:disabled):hover": { borderColor: C.danger, color: C.danger },
    },

    "& .activeBadge": {
      background: "#fffaf0",
      border: "1px solid #fcd9a4",
      borderRadius: 10,
      padding: "6px 12px",
      fontSize: "0.72vw",
      lineHeight: 1.5,
      color: C.orange,
      fontWeight: 600,
      "& strong": { fontWeight: 800 },
    },
  },

  // Botão que revela o painel. Discreto: some (opacidade baixa via style
  // inline) quando não há filtro em uso e volta ao passar o mouse.
  dateFilterToggle: {
    position: "fixed",
    right: 14,
    bottom: 14,
    zIndex: 901,
    padding: "8px 16px",
    borderRadius: 999,
    border: `1px solid ${C.border}`,
    background: C.card,
    color: C.muted,
    fontFamily: "inherit",
    fontSize: "0.72vw",
    fontWeight: 700,
    letterSpacing: ".03em",
    cursor: "pointer",
    boxShadow: "0 2px 10px rgba(16,24,40,.14)",
    transition: "opacity .3s, border-color .12s, color .12s",

    "&:hover": {
      opacity: "1 !important",
      borderColor: C.slate2,
      color: C.slate,
    },

    // Filtro em uso: laranja, mesma semântica dos alertas do header
    "&.active": {
      background: "#fffaf0",
      borderColor: "#fcd9a4",
      color: C.orange,
      "&:hover": { borderColor: C.orange, color: C.orange },
    },
  },

  tablesRow: {
    display: "flex",
    gap: 15,
    alignItems: "flex-start",
  },

  // ── TABELA principal ────────────────────────────────────────────────────
  boxTable: {
    flex: 1,
    height: "max-content",
    display: "flex",
    flexDirection: "column",
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    boxShadow: "0 1px 3px rgba(16,24,40,.06)",
    overflow: "hidden",
  },

  // ── TABELA de PREVISÃO (isolada, própria caixa) ─────────────────────────
  boxTableForecast: {
    flex: "0 0 12vw",
    height: "max-content",
    display: "flex",
    flexDirection: "column",
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    boxShadow: "0 1px 3px rgba(16,24,40,.06)",
    overflow: "hidden",
  },

  tableHeadForecast: {
    display: "flex",
    background: "#94a3b8",
    "& span": {
      flex: 1,
      padding: "10px 4px",
      color: "#fff",
      textAlign: "center",
      fontWeight: 800,
      fontSize: "0.55vw",
      textTransform: "uppercase",
      letterSpacing: ".04em",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  },

  tableValuesForecast: {
    "& p": {
      display: "flex",
      background: C.card,
      borderBottom: `1px solid ${C.line}`,
      transition: "background .12s",

      "&:nth-child(odd)": { background: "#f8fafc" },
      "&:hover": { background: "#eef6ff" },

      // Acompanha a altura da linha expressa na tabela ao lado.
      "&.expressRow": { minHeight: EXPRESS_ROW_HEIGHT },

      "& span": {
        flex: 1,
        padding: "8px 4px",
        textAlign: "center",
        fontSize: TABLE_FONT_SIZE,
        lineHeight: 1,
        fontWeight: 700,
        color: C.slate2,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    },
  },

  lineTotalsForecast: {
    display: "flex",
    background: "#cbd5e1",
    borderTop: `2px solid ${C.border}`,

    "& span": {
      flex: 1,
      padding: "12px 4px",
      textAlign: "center",
      fontSize: "1.15vw",
      fontWeight: 800,
      color: C.slate,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  },

  tableHead: {
    display: "flex",
    background: C.slate,

    "& span": {
      flex: 1,
      padding: "10px 4px",
      color: "#cbd5e1",
      textAlign: "center",
      fontWeight: 700,
      fontSize: "0.55vw",
      textTransform: "uppercase",
      letterSpacing: ".04em",
      borderRight: "1px solid rgba(255,255,255,.06)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      "&:last-child": { borderRight: "none" },

      "&.labelEarlyShipping": { color: C.orangeSoft },
      "&.labelLateShipping": { color: "#f87171" },
      "&.labelOnTimeShipping": { color: C.greenSoft },

      "&.colDelayed": {
        background: C.danger,
        color: "#fff",
      },

      // Coluna TRANSPORTADORA: mais largura, sem quebra de linha
      "&.nameTimeCollection": {
        flex: "0 0 auto",
        whiteSpace: "nowrap",
      },
      // A primeira .nameTimeCollection é a COLETA (compacta),
      // a segunda é o nome da transportadora (mais larga).
      "&.nameTimeCollection:nth-of-type(1)": { minWidth: "5vw", flex: "0 0 5vw" },
      "&.nameTimeCollection:nth-of-type(2)": { minWidth: "11vw", flex: "0 0 11vw" },

      // Coluna PREVISÃO: isolada da tabela via marginLeft + fundo cinza slate
      "&.colForecast": {
        marginLeft: 15,
        background: C.slate2,
        color: "#e2e8f0",
        borderLeft: "3px solid rgba(255,255,255,.18)",
        fontWeight: 800,
      },
    },
  },

  tableValues: {
    "& p": {
      position: "relative",
      zIndex: 0,
      display: "flex",
      background: C.card,
      borderBottom: `1px solid ${C.line}`,
      transition: "background .12s",

      "&:nth-child(odd)": {
        background: "#f8fafc",
        "& span.colForecast": { background: "#eef2f7" },
      },

      "&:hover": {
        background: "#eef6ff",
      },

      // ── LINHA DA ENTREGA EXPRESSA ─────────────────────────────────────
      // A transportadora expressa conta prazo em horas, então a linha quebra
      // em três faixas de urgência. Cada coluna operacional empilha os três
      // valores, alinhados com os rótulos na célula da transportadora.
      "&.expressRow": {
        minHeight: EXPRESS_ROW_HEIGHT,
        "& > span": { paddingTop: 4, paddingBottom: 4 },

        // A célula da transportadora precisa ser flex pro bloco de rótulos
        // encostar à direita, ao lado do nome. O nowrap herdado faria o nome
        // mais os rótulos transbordarem por cima das colunas vizinhas — aqui
        // o nome quebra em duas linhas, que a linha expressa tem altura de
        // sobra.
        "& > span.nameTimeCollection:nth-of-type(2)": {
          display: "flex",
          alignItems: "center",
          whiteSpace: "normal",
        },
        "& .expressName": {
          fontStyle: "normal",
          flex: 1,
          minWidth: 0,
          textAlign: "left",
          lineHeight: 1.15,
        },

        // Rótulos à direita do nome: ATRASADO / FALTAM 30M / NO PRAZO
        "& .expressLabels": {
          display: "flex",
          flexDirection: "column",
          // 16, e não EXPRESS_STACK_GAP: o rótulo tem fonte menor que o
          // número, então a linha dele é mais baixa. Com o mesmo gap dos
          // dois lados, ATRASADO ia subindo em relação ao valor a cada
          // faixa. Este número compensa a diferença — se a fonte do rótulo
          // ou a da tabela mudar, ele precisa ser reajustado junto.
          gap: 16,
          flexShrink: 0,
          marginLeft: "auto",
          paddingLeft: 8,
          textAlign: "right",
        },
        "& .expressLabel": {
          fontStyle: "normal",
          fontSize: "0.56vw",
          fontWeight: 800,
          letterSpacing: ".04em",
          lineHeight: "1.55em",
          color: C.muted,
          "&.atrasado": { color: C.danger },
          "&.faltam30": { color: C.orange },
        },

        // Pilha de valores de uma coluna operacional
        "& .expressStack": {
          display: "flex",
          flexDirection: "column",
          gap: 6,
          width: "100%",
          fontStyle: "normal",
        },
        "& .expressCell": {
          fontStyle: "normal",
          lineHeight: "1.55em",
          borderRadius: 5,
          fontWeight: 700,
        },

        // ATRASADO com pedido: fundo vermelho fixo, sem piscar. É um estado
        // consumado — já perdeu o prazo. Zerado fica igual ao "no prazo".
        "& .expressCell.atrasado.hasValue": {
          background: C.danger,
          color: "#fff",
        },

        // FALTAM 30M não tem fundo próprio: o laranja fica só no rótulo. O
        // alerta aqui é o pisca, aplicado na faixa inteira pelo <Row> (ver
        // $expressWarning), porque ainda dá tempo de agir.
        "& .expressCell.faltam30": { borderRadius: 5 },
      },

      "&.collectionFinish": {
        "& span.nameTimeCollection": { color: "#9aa4b2" },
        // Celula com fundo vermelho: TODO o texto fica branco, inclusive o
        // "(n)" de atrasados — senao ele some no proprio vermelho do fundo.
        "& span.ordersRisk": {
          background: C.danger,
          color: "#fff",
          borderRadius: 6,
        },
        "& span.ordersRisk em.delayedCount": { color: "#fff !important" },
      },

      // Linha da proxima coleta (piscando em vermelho): TODO o texto vai pra
      // cor escura padrao. Sem o !important os rotulos coloridos (atrasados,
      // porte da transportadora, envios) ficam ilegiveis sobre o vermelho.
      "&.collectionInTime": {
        background: C.danger,
        "& span": { color: "#000 !important" },
        "& span em.delayedCount": { color: "#000 !important" },
      },

      // ── PORTE DO PEDIDO ────────────────────────────────────────────────
      // Borda lateral e texto de COLETA/TRANSPORTADORA na cor do porte.
      "&.sizePequeno": {
        borderLeft: `4px solid ${ORDER_SIZE_COLORS.pequeno}`,
        "& span.nameTimeCollection": { color: ORDER_SIZE_COLORS.pequeno, fontWeight: 700 },
      },
      "&.sizeMedio": {
        borderLeft: `4px solid ${ORDER_SIZE_COLORS.medio}`,
        "& span.nameTimeCollection": { color: ORDER_SIZE_COLORS.medio, fontWeight: 700 },
      },
      "&.sizeGrande": {
        borderLeft: `4px solid ${ORDER_SIZE_COLORS.grande}`,
        "& span.nameTimeCollection": { color: ORDER_SIZE_COLORS.grande, fontWeight: 700 },
      },

      // Coleta JA PASSOU: mantem a cor do porte, so esmaecida (a regra de
      // duas classes vence o cinza padrao de .collectionFinish).
      "&.collectionFinish.sizePequeno": {
        borderLeftColor: ORDER_SIZE_COLORS.pequeno + "66",
        "& span.nameTimeCollection": { color: ORDER_SIZE_COLORS.pequeno, opacity: 0.45 },
      },
      "&.collectionFinish.sizeMedio": {
        borderLeftColor: ORDER_SIZE_COLORS.medio + "66",
        "& span.nameTimeCollection": { color: ORDER_SIZE_COLORS.medio, opacity: 0.45 },
      },
      "&.collectionFinish.sizeGrande": {
        borderLeftColor: ORDER_SIZE_COLORS.grande + "66",
        "& span.nameTimeCollection": { color: ORDER_SIZE_COLORS.grande, opacity: 0.45 },
      },

      "& span": {
        flex: 1,
        padding: "8px 4px",
        textAlign: "center",
        fontSize: TABLE_FONT_SIZE,
        lineHeight: 1,
        fontWeight: 600,
        color: C.text,
        borderRight: `1px solid ${C.line}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        "&:not(.nameTimeCollection)": { cursor: "pointer" },
        "&:last-child": { borderRight: "none" },

        // Coluna COLETA + TRANSPORTADORA: sem quebra + width fixo maior pra
        // transportadora, pra caber "SHOPEE COLETA", "TROCA LALAMOVE" etc.
        "&.nameTimeCollection": {
          fontWeight: 700,
          color: C.slate,
          cursor: "default",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          flex: "0 0 auto",
        },
        "&.nameTimeCollection:nth-of-type(1)": { minWidth: "5vw", flex: "0 0 5vw" },
        "&.nameTimeCollection:nth-of-type(2)": {
          minWidth: "11vw",
          flex: "0 0 11vw",
          justifyContent: "flex-start",
          paddingLeft: 12,
        },

        "&.labelEarlyShipping": { color: C.orange, fontWeight: 700 },
        "&.labelOnTimeShipping": { color: C.green, fontWeight: 700 },
        "&.labelLateShipping": { color: C.danger, fontWeight: 700 },
        "&.colDelayed": { color: C.danger, fontWeight: 800 },

        // "(n)" ao lado do numero da etapa: quantos daqueles pedidos estao
        // com a data de embarque vencida. Mesma cor da coluna ATRASADOS.
        "& em.delayedCount": {
          fontStyle: "normal",
          fontWeight: 800,
          color: `${C.danger} !important`,
          marginLeft: 4,
          fontSize: "0.85em",
        },

        // Coluna PREVISÃO: isolada da tabela via marginLeft, fundo cinza
        "&.colForecast": {
          marginLeft: 15,
          background: "#f4f6fa",
          color: C.slate2,
          fontWeight: 700,
          borderLeft: "3px solid #e2e8f0",
        },
      },
    },
  },

  // ── RESUMO POR PORTE DO PEDIDO (abaixo da tabela principal) ──────────────
  // Card próprio — separado do card das transportadoras
  // ── RESUMO POR PORTE DO PEDIDO ─────────────────────────────────────────
  // Faixa horizontal ocupando a largura toda embaixo da tabela: um bloco por
  // porte + um bloco final com os totais gerais. Na vertical o painel estourava
  // a altura da tela e o último porte ficava cortado.
  sizeSummary: {
    display: "flex",
    width: "100%",
    gap: 12,
  },

  sizeCard: {
    flex: 1,
    minWidth: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(16,24,40,.06)",
    display: "flex",
    flexDirection: "column",
    color: "#fff",

    "& .cardTitle": {
      padding: "5px 12px",
      fontSize: "0.95vw",
      fontWeight: 800,
      letterSpacing: ".06em",
      textAlign: "center",
      textTransform: "uppercase",
      background: "rgba(0,0,0,.14)",
    },

    "& .cardMetrics": {
      flex: 1,
      display: "flex",
    },

    "& .metric": {
      flex: 1,
      minWidth: 0,
      padding: "6px 4px 8px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      borderRight: "1px solid rgba(255,255,255,.28)",
      "&:last-child": { borderRight: "none" },
    },
    "& .metricLabel": {
      fontSize: "0.6vw",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: ".08em",
      color: "rgba(255,255,255,.8)",
    },
    "& .metricValue": {
      fontSize: "1.5vw",
      fontWeight: 800,
      lineHeight: 1.05,
      marginTop: 2,
    },

    // Mesmas cores de porte usadas na borda e no texto das linhas da tabela
    "&.grande": { background: ORDER_SIZE_COLORS.grande },
    "&.medio": { background: ORDER_SIZE_COLORS.medio },
    "&.pequeno": { background: ORDER_SIZE_COLORS.pequeno },
    "&.total": { background: C.slate },
  },

  lineTotals: {
    display: "flex",
    background: "#e2e8f0",
    borderTop: `2px solid ${C.border}`,

    "& span": {
      flex: 1,
      padding: "12px 4px",
      textAlign: "center",
      fontSize: "1.15vw",
      fontWeight: 800,
      color: C.slate,
      borderRight: "1px solid #d5dce6",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      "&:last-child": { borderRight: "none" },

      // Alinha as duas primeiras células ao mesmo width das colunas da tabela
      "&:nth-of-type(1)": { flex: "0 0 5vw", minWidth: "5vw" },
      "&:nth-of-type(2)": { flex: "0 0 11vw", minWidth: "11vw" },

      "&.labelTotal": { background: C.slate, color: "#fff" },
      // TOTAL clicável — abre o modal com todos os pedidos da tabela
      "&.clickable": {
        cursor: "pointer",
        transition: "filter .12s, box-shadow .12s",
        "&:hover": {
          filter: "brightness(1.35)",
          boxShadow: "inset 0 0 0 2px rgba(255,255,255,.55)",
        },
      },
      "&.labelOnTimeShipping": { color: C.green },
      "&.labelEarlyShipping": { color: C.orange },
      "&.labelLateShipping": { color: C.danger },

      // "(n)" de atrasados no rodape, somando as linhas acima
      "& em.delayedCount": {
        fontStyle: "normal",
        fontWeight: 800,
        color: `${C.danger} !important`,
        marginLeft: 4,
        fontSize: "0.85em",
      },
      // !important garante branco no fundo vermelho — sobrescreve qualquer
      // regra de cor que possa herdar de outra parte da cascata
      "&.colDelayed": {
        background: `${C.danger} !important`,
        color: "#fff !important",
      },
      "&.colForecast": {
        marginLeft: 15,
        background: C.forecast,
        color: "#fff",
        borderLeft: "3px solid #cbd5e1",
      },
    },
  },

  // ── MODAL ────────────────────────────────────────────────────────────────
  modalContainer: {
    position: "fixed",
    inset: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "rgba(15,23,42,.55)",
    backdropFilter: "blur(3px)",
    zIndex: 2000,
    padding: 20,
  },

  modal: {
    width: "85%",
    height: "80%",
    background: C.card,
    borderRadius: 16,
    boxShadow: "0 24px 64px rgba(0,0,0,.35)",
    padding: 22,
    display: "flex",
    flexDirection: "column",
    fontFamily: "'Inter', 'Segoe UI', Roboto, Arial, sans-serif",
  },

  modalTabs: {
    display: "flex",
    gap: 4,
    marginBottom: 12,
    borderBottom: `2px solid ${C.border}`,

    "& button": {
      background: "transparent",
      border: "none",
      padding: "10px 18px",
      fontSize: 14,
      fontWeight: 700,
      color: C.muted,
      cursor: "pointer",
      textTransform: "uppercase",
      letterSpacing: ".04em",
      borderBottom: "3px solid transparent",
      marginBottom: -2,
      transition: "color .12s, border-color .12s",
      fontFamily: "'Inter', 'Segoe UI', Roboto, Arial, sans-serif",

      "& .count": {
        display: "inline-block",
        marginLeft: 6,
        padding: "2px 8px",
        background: C.line,
        color: C.slate2,
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 800,
      },

      "&.active": {
        color: C.slate,
        borderBottomColor: C.slate,
        "& .count": {
          background: C.slate,
          color: "#fff",
        },
      },

      "&:hover:not(.active)": {
        color: C.slate2,
      },
    },
  },

  headModal: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 18,

    "& h1": {
      fontSize: 22,
      fontWeight: 800,
      color: C.slate,
    },

    "& span": {
      width: 36,
      height: 36,
      borderRadius: "50%",
      background: "#f1f5f9",
      color: C.slate,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 18,
      fontWeight: 700,
      cursor: "pointer",
      transition: ".12s",
      "&:hover": { background: "#e2e8f0" },
    },
  },

  tableModal: {
    width: "100%",
    flex: 1,
    overflowY: "auto",
    border: `1px solid ${C.border}`,
    borderRadius: 10,

    "&::-webkit-scrollbar": { width: 8 },
    "&::-webkit-scrollbar-thumb": { background: "#cbd5e1", borderRadius: 4 },

    "& .headTableModal, & .bodyTableModal": {
      "& p": {
        display: "flex",
        "& span": {
          textAlign: "center",
          fontWeight: 600,
          padding: "9px 10px",
          fontSize: 13,
        },
      },
    },

    "& .headTableModal": {
      position: "sticky",
      top: 0,
      zIndex: 1,
      "& p": {
        background: C.slate,
        "& span": {
          color: "#fff",
          fontWeight: 700,
          textTransform: "uppercase",
          fontSize: 11,
          letterSpacing: ".04em",
          // 9 colunas: COLETA · STATUS · CRIAÇÃO · EMBALAGEM · EMBARQUE · PREV. ENTREGA · TRANSP · PEDIDO · AÇÕES
          "&:nth-child(1)": { width: "6%" },
          "&:nth-child(2)": { width: "11%" },
          "&:nth-child(3)": { width: "11%" },
          "&:nth-child(4)": { width: "11%" },
          "&:nth-child(5)": { width: "11%" },
          "&:nth-child(6)": { width: "9%" },
          "&:nth-child(7)": { width: "20%", textAlign: "left" },
          "&:nth-child(8)": { width: "9%" },
          "&:nth-child(9)": { width: "12%" },
        },
      },
    },

    "& .bodyTableModal": {
      "& p": {
        background: C.card,
        borderBottom: `1px solid ${C.line}`,
        "&:nth-child(odd)": { background: "#f8fafc" },

        // Pedido com a data de embarque vencida — vence a zebra das ímpares
        "&.delayedRow": { background: "#fe00000f !important" },

        // ENTREGA EXPRESSA — o prazo é de horas, então o destaque precisa ser
        // mais forte que o da lista normal: nesses dois a ação é agora.
        // Barra lateral + fundo, porque só o fundo claro se perde na zebra.
        "&.expressLate": {
          background: "#fee2e2 !important",
          boxShadow: `inset 4px 0 0 ${C.danger}`,
          "& span": { fontWeight: 700 },
        },
        "&.expressWarning": {
          background: "#fff4e5 !important",
          boxShadow: `inset 4px 0 0 ${C.orange}`,
          "& span": { fontWeight: 600 },
        },

        "& span": {
          color: C.text,
          "&:nth-child(1)": { width: "6%" },
          "&:nth-child(2)": { width: "11%" },
          "&:nth-child(3)": { width: "11%" },
          "&:nth-child(4)": { width: "11%" },
          "&:nth-child(5)": { width: "11%" },
          "&:nth-child(6)": { width: "9%" },
          "&:nth-child(7)": { width: "20%", textAlign: "left" },
          "&:nth-child(8)": { width: "9%" },
          "&:nth-child(9)": { width: "12%" },
          "&.SupplierNameCorporateName": { fontWeight: 700, color: C.slate },
          "&.statusCell": {
            fontSize: "10.5px !important",
            fontWeight: "700 !important",
            color: `${C.slate2} !important`,
            textTransform: "uppercase",
            letterSpacing: ".02em",
          },
        },
      },
    },
  },

  // ── VISÃO ANALÍTICA (dentro do modal de listagem) ────────────────────
  modalViewSwitch: {
    display: "flex",
    gap: 6,
    marginBottom: 12,
    flexShrink: 0, // é irmão do scroller no flex do modal

    "& button": {
      padding: "7px 16px",
      borderRadius: 8,
      border: `1px solid ${C.border}`,
      background: C.card,
      color: C.muted,
      fontSize: 12.5,
      fontWeight: 700,
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "background .12s, color .12s, border-color .12s",
      "&:hover": { borderColor: C.slate2, color: C.slate },
      "&.active": { background: C.slate, borderColor: C.slate, color: "#fff" },
    },
  },

  modalEmptyBox: {
    padding: "48px 20px",
    textAlign: "center",
    color: C.muted,
    fontStyle: "italic",
  },

  // Filho direto do flex do modal: ocupa a altura restante e rola por dentro,
  // igual ao tableModal da lista. Sem flex/minHeight o conteúdo cresce e
  // vaza pra fora do modal, que tem altura fixa (80%).
  analyticsWrap: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    paddingRight: 4,
    paddingBottom: 4,

    "&::-webkit-scrollbar": { width: 8 },
    "&::-webkit-scrollbar-thumb": { background: "#cbd5e1", borderRadius: 4 },

    // Sem isso os blocos se comprimem no lugar de gerar scroll
    "& > *": { flexShrink: 0 },
  },

  // Faixa de KPIs: quebra sozinha e cada card ocupa no mínimo 160px
  analyticsKpis: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 10,

    "& .kpiBox": {
      background: C.card,
      border: `1px solid ${C.border}`,
      borderLeft: `4px solid ${C.border}`,
      borderRadius: 10,
      padding: "10px 14px",
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
    },
    "& .kpiLabel": {
      fontSize: 10.5,
      fontWeight: 700,
      color: C.muted,
      textTransform: "uppercase",
      letterSpacing: ".06em",
    },
    "& .kpiValue": {
      fontSize: 24,
      fontWeight: 800,
      color: C.text,
      lineHeight: 1.15,
      marginTop: 2,
      "&.money": { fontSize: 19 },
    },
    "& .kpiHint": {
      fontSize: 11,
      color: C.muted,
      marginTop: 1,
    },

    "& .kpiBox.ok": { borderLeftColor: C.green, "& .kpiValue": { color: C.green } },
    "& .kpiBox.warn": { borderLeftColor: C.orangeSoft, "& .kpiValue": { color: C.orange } },
    "& .kpiBox.danger": { borderLeftColor: C.danger, "& .kpiValue": { color: C.danger } },
  },

  analyticsAlerts: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",

    "& .alertChip": {
      background: "#fffaf0",
      border: "1px solid #fcd9a4",
      borderRadius: 8,
      padding: "6px 12px",
      fontSize: 12,
      fontWeight: 700,
      color: C.orange,
    },
  },

  analyticsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 14,
  },

  analyticsPanel: {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    padding: "12px 14px",
    minWidth: 0,

    "& h4": {
      fontSize: 11,
      fontWeight: 800,
      color: C.slate,
      textTransform: "uppercase",
      letterSpacing: ".06em",
      marginBottom: 10,
    },
    "& .panelHint": {
      fontWeight: 600,
      color: C.muted,
      textTransform: "none",
      letterSpacing: 0,
      marginLeft: 6,
      fontSize: 10.5,
    },
  },

  timingGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 10,

    "& .timingBox": {
      borderRadius: 10,
      padding: "10px 12px",
      display: "flex",
      flexDirection: "column",
      background: "#f8fafc",
      borderLeft: "4px solid #cbd5e1",
      minWidth: 0,
    },
    "& .timingLabel": {
      fontSize: 10,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: ".05em",
      color: C.muted,
    },
    "& .timingValue": { fontSize: 22, fontWeight: 800, color: C.text, lineHeight: 1.15 },
    "& .timingHint": { fontSize: 11, color: C.muted },
    "& .timingMoney": {
      fontSize: 12.5,
      fontWeight: 800,
      color: C.slate2,
      marginTop: 3,
      paddingTop: 3,
      borderTop: `1px dashed ${C.border}`,
    },

    "& .ok": { borderLeftColor: C.green, "& .timingValue": { color: C.green } },
    "& .warn": { borderLeftColor: C.orangeSoft, "& .timingValue": { color: C.orange } },
    "& .danger": { borderLeftColor: C.danger, "& .timingValue": { color: C.danger } },
  },

  analyticsTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12.5,

    "& th": {
      textAlign: "left",
      fontSize: 10,
      fontWeight: 800,
      color: C.muted,
      textTransform: "uppercase",
      letterSpacing: ".05em",
      padding: "0 8px 6px",
      borderBottom: `1px solid ${C.border}`,
      "&.num": { textAlign: "right" },
    },
    "& td": {
      padding: "7px 8px",
      color: C.text,
      borderBottom: `1px solid ${C.line}`,
      "&.num": { textAlign: "right", fontWeight: 700, whiteSpace: "nowrap" },
      "&.danger": { color: C.danger, fontWeight: 800 },
    },
    "& tbody tr:last-child td": { borderBottom: "none" },
    "& tbody tr:hover": { background: "#f8fafc" },
  },

  // Barra proporcional embaixo do rótulo — mostra a concentração sem gráfico
  shareBar: {
    display: "block",
    height: 3,
    marginTop: 4,
    borderRadius: 2,
    background: C.line,
    overflow: "hidden",

    "& span": {
      display: "block",
      height: "100%",
      background: C.slate2,
      borderRadius: 2,
    },
  },

  // ── BUSCA DENTRO DO MODAL ────────────────────────────────────────────
  modalSearchWrap: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    marginBottom: 12,
  },

  modalSearchInput: {
    flex: 1,
    padding: "10px 14px",
    fontSize: 13,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    background: "#f8fafc",
    color: C.slate,
    outline: "none",
    fontFamily: "inherit",
    transition: "border-color .12s, background .12s",
    "&:focus": {
      background: "#fff",
      borderColor: C.slate,
      boxShadow: "0 0 0 3px rgba(30,41,59,.08)",
    },
    "&::placeholder": {
      color: C.muted,
    },
  },

  modalSearchCount: {
    fontSize: 12,
    color: C.muted,
    fontWeight: 600,
    whiteSpace: "nowrap",
    "& strong": {
      color: C.slate,
      fontSize: 14,
      marginRight: 3,
    },
  },

  modalEmpty: {
    display: "flex !important",
    justifyContent: "center",
    padding: "40px 20px",
    background: `${C.card} !important`,
    "& span": {
      width: "auto !important",
      color: `${C.muted} !important`,
      fontStyle: "italic",
      fontSize: "13px !important",
    },
  },

  // ── Filtro de status dentro do modal ─────────────────────────────────
  modalStatusFilter: {
    padding: "10px 14px",
    fontSize: 13,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    background: "#f8fafc",
    color: C.slate,
    outline: "none",
    fontFamily: "inherit",
    cursor: "pointer",
    minWidth: 180,
    "&:focus": { background: "#fff", borderColor: C.slate },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },

  // ── Sub-tabs dentro da Visão Geral (Por Dia / Por Status) ────────────
  modalSubTabs: {
    display: "flex",
    gap: 4,
    marginBottom: 12,
    padding: 4,
    background: C.line,
    borderRadius: 8,
    width: "fit-content",

    "& button": {
      background: "transparent",
      border: "none",
      padding: "6px 16px",
      fontSize: 12,
      fontWeight: 700,
      color: C.muted,
      cursor: "pointer",
      borderRadius: 6,
      fontFamily: "inherit",
      textTransform: "uppercase",
      letterSpacing: ".04em",
      transition: "all .12s",

      "&.active": {
        background: C.card,
        color: C.slate,
        boxShadow: "0 1px 3px rgba(16,24,40,.08)",
      },
      "&:hover:not(.active)": { color: C.slate2 },
    },
  },

  // ── Tabela pivot da Visão Geral ──────────────────────────────────────
  pivotWrap: {
    flex: 1,
    overflow: "auto",
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    "&::-webkit-scrollbar": { width: 8, height: 8 },
    "&::-webkit-scrollbar-thumb": { background: "#cbd5e1", borderRadius: 4 },
  },

  pivotTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,

    "& th, & td": {
      padding: "10px 14px",
      textAlign: "center",
      border: `1px solid ${C.line}`,
    },

    "& thead th": {
      background: C.slate,
      color: "#fff",
      fontWeight: 700,
      fontSize: 11,
      textTransform: "uppercase",
      letterSpacing: ".04em",
      position: "sticky",
      top: 0,
      zIndex: 1,
    },
    "& thead th.rowLabel": {
      textAlign: "left",
    },
    "& thead th.totalCol": {
      background: C.slate2,
    },

    "& tbody tr": {
      "&:nth-child(odd)": { background: "#f8fafc" },
      "&:hover": { background: "#eef6ff" },
    },
    "& tbody td.rowLabel": {
      textAlign: "left",
      fontWeight: 700,
      color: C.slate,
      background: "#f4f6fa",
      whiteSpace: "nowrap",
    },
    "& tbody td.totalCol": {
      fontWeight: 800,
      color: C.slate,
      background: "#e2e8f0",
    },

    // Peças / valor entre parênteses junto da contagem de pedidos.
    // Em bloco, logo abaixo do número: com a medida em TODAS as células,
    // inline alargaria demais cada coluna de dia.
    "& .pivotWeight": {
      display: "block",
      fontStyle: "normal",
      fontWeight: 700,
      fontSize: "0.84em",
      color: C.muted,
      marginTop: 1,
      whiteSpace: "nowrap",
    },
    "& tbody td.totalCol .pivotWeight": {
      color: C.slate2,
    },
    "& tfoot .pivotWeight": {
      color: "rgba(255,255,255,.75)",
    },
    "& tbody td.empty": {
      padding: "40px 20px",
      color: C.muted,
      fontStyle: "italic",
    },

    "& tfoot td": {
      background: C.slate,
      color: "#fff",
      fontWeight: 800,
      fontSize: 13,
      // Fixa a linha de subtotais no rodapé enquanto o corpo faz scroll
      position: "sticky",
      bottom: 0,
      zIndex: 1,
    },
    "& tfoot td.rowLabel": {
      textAlign: "left",
      textTransform: "uppercase",
      letterSpacing: ".04em",
    },
    "& tfoot td.totalCol": {
      background: "#0f172a",
    },
  },

  // ── RECALCULAR: botão em massa (topo) + banner de progresso ──────────
  recalcAllBtn: {
    padding: "10px 16px",
    fontSize: 12,
    fontWeight: 700,
    background: C.slate,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    whiteSpace: "nowrap",
    letterSpacing: ".02em",
    transition: "background .12s, transform .12s",
    "&:hover:not(:disabled)": {
      background: "#0f172a",
      transform: "translateY(-1px)",
    },
    "&:disabled": {
      background: "#94a3b8",
      color: "#fff",
      cursor: "not-allowed",
    },
  },

  recalcProgress: {
    marginBottom: 12,
    padding: "10px 14px",
    background: "#fffbeb",
    border: "1px solid #fcd34d",
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  recalcProgressText: {
    fontSize: 12,
    fontWeight: 700,
    color: "#92400e",
    "& strong": {
      fontFamily: "'Space Mono', 'Roboto Mono', monospace",
      color: "#78350f",
    },
  },

  recalcProgressBar: {
    height: 6,
    background: "#fef3c7",
    borderRadius: 3,
    overflow: "hidden",
  },

  recalcProgressFill: {
    height: "100%",
    background: "#f59e0b",
    borderRadius: 3,
    transition: "width .3s ease-out",
  },

  // ── RECALCULAR: botão por linha (na coluna AÇÕES) ────────────────────
  recalcRowBtn: {
    padding: "5px 10px",
    fontSize: 11,
    fontWeight: 700,
    background: "transparent",
    color: "#2563eb",
    border: "1px solid #93c5fd",
    borderRadius: 6,
    cursor: "pointer",
    whiteSpace: "nowrap",
    letterSpacing: ".02em",
    transition: "background .12s, border-color .12s",
    fontFamily: "inherit",
    "&:hover:not(:disabled)": {
      background: "#eff6ff",
      borderColor: "#2563eb",
    },
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },

  // ── MOBILE ───────────────────────────────────────────────────────────────
  mobile: {
    [theme.breakpoints.up("sm")]: { display: "none" },

    display: "flex",
    flexWrap: "wrap",
    padding: 14,
    gap: 14,
    background: C.bg,
    minHeight: "100vh",
    boxSizing: "border-box",
    fontFamily: "'Inter', 'Segoe UI', Roboto, Arial, sans-serif",

    "& h1": {
      padding: 12,
      width: "100%",
      background: C.slate,
      color: "#fff",
      textAlign: "center",
      borderRadius: 12,
      fontSize: 18,
      fontWeight: 800,
      letterSpacing: ".02em",

      "&.shipped": { background: "#0f172a" },
    },

    "& div": {
      flex: "0 0 calc(50% - 7px)",
      padding: 16,
      textAlign: "center",
      borderRadius: 14,
      border: `1px solid ${C.border}`,
      background: C.card,
      boxShadow: "0 1px 3px rgba(16,24,40,.06)",
      boxSizing: "border-box",

      "& h2": { fontSize: "3.4vw", color: C.muted, fontWeight: 700 },
      "& p": { fontSize: "9vw", fontWeight: 800, color: C.text },

      "&.full": { flex: "none", width: "100%" },

      "&.earlyShipping": {
        background: C.orange,
        "& p, & h2": { color: "#fff" },
      },
      "&.lateShipping": {
        background: C.danger,
        "& p, & h2": { color: "#fff" },
      },
      "&.delayed": {
        background: C.danger,
        "& p, & h2": { color: "#fff" },
      },
      "&.forecast": {
        background: C.forecast,
        "& p, & h2": { color: "#fff" },
      },
      "&.inTime": {
        width: "100%",
        flex: "none",
        background: C.green,
        "& p, & h2": { color: "#fff" },
      },
    },
  },
}));

export default useStyle;
